import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { elevatedAccessApi } from '../services/elevatedAccessService';
import {
  setRoleScreensWithApiSync,
  toggleScreenRoleWithApiSync,
} from '../utils/authorizations/syncEndpointsFromScreens';

function sortUnique(values) {
  return [...new Set(values.map((v) => v.trim()).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b),
  );
}

function toggleRole(rows, rowId, role) {
  return rows.map((row) => {
    if (row.id !== rowId) return row;
    const has = row.allowed_roles.includes(role);
    return {
      ...row,
      allowed_roles: has
        ? row.allowed_roles.filter((r) => r !== role)
        : sortUnique([...row.allowed_roles, role]),
    };
  });
}

function setAllRoles(rows, rowId, roles, checked) {
  return rows.map((row) => {
    if (row.id !== rowId) return row;
    return { ...row, allowed_roles: checked ? [...roles] : [] };
  });
}

export function useAuthorizationsConfigPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('principals');
  const [config, setConfig] = useState(null);
  const [roles, setRoles] = useState([]);
  const [matrixRoles, setMatrixRoles] = useState([]);
  const [emails, setEmails] = useState([]);
  const [screens, setScreens] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [roleDraft, setRoleDraft] = useState('');
  const [emailDraft, setEmailDraft] = useState('');
  const [screenFilter, setScreenFilter] = useState('');
  const [endpointFilter, setEndpointFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await elevatedAccessApi.get();
      setConfig(data);
      setRoles(sortUnique(data.org_roles || []));
      setEmails(sortUnique(data.emails || []));
      setMatrixRoles(sortUnique(data.matrix_roles || data.org_roles || []));
      setScreens(data.screens || []);
      setEndpoints(data.endpoints || []);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to load authorisations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const addRole = () => {
    const value = roleDraft.trim().toLowerCase();
    if (!value) return;
    setRoles((prev) => sortUnique([...prev, value]));
    setRoleDraft('');
  };

  const removeRole = (value) => setRoles((prev) => prev.filter((r) => r !== value));

  const addEmail = () => {
    const value = emailDraft.trim().toLowerCase();
    if (!value || !value.includes('@')) {
      toast.error('Enter a valid email');
      return;
    }
    setEmails((prev) => sortUnique([...prev, value]));
    setEmailDraft('');
  };

  const removeEmail = (value) => setEmails((prev) => prev.filter((e) => e !== value));

  const toggleScreenRole = (screenId, role) => {
    const next = toggleScreenRoleWithApiSync(screens, endpoints, screenId, role);
    setScreens(next.screens);
    setEndpoints(next.endpoints);
  };

  const setAllScreensForRole = (role, checked) => {
    const q = screenFilter.trim().toLowerCase();
    const visibleIds = screens
      .filter((screen) => {
        if (!q) return true;
        return (
          screen.label.toLowerCase().includes(q) ||
          (screen.path || '').toLowerCase().includes(q) ||
          screen.group.toLowerCase().includes(q)
        );
      })
      .map((screen) => screen.id);
    const next = setRoleScreensWithApiSync(screens, endpoints, role, visibleIds, checked);
    setScreens(next.screens);
    setEndpoints(next.endpoints);
  };

  const handleSave = async () => {
    if (!roles.length && !emails.length) {
      toast.error('Add at least one role or email');
      return;
    }
    setSaving(true);
    try {
      const saved = await elevatedAccessApi.save({
        org_roles: roles,
        emails,
        screens: screens.map((s) => ({ id: s.id, allowed_roles: s.allowed_roles })),
        endpoints: endpoints.map((e) => ({ id: e.id, allowed_roles: e.allowed_roles })),
      });
      setConfig(saved);
      setRoles(sortUnique(saved.org_roles || []));
      setEmails(sortUnique(saved.emails || []));
      setMatrixRoles(sortUnique(saved.matrix_roles || saved.org_roles || []));
      setScreens(saved.screens || []);
      setEndpoints(saved.endpoints || []);
      toast.success('Authorisations saved');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return {
    loading,
    saving,
    tab,
    setTab,
    config,
    roles,
    matrixRoles,
    emails,
    screens,
    endpoints,
    roleDraft,
    setRoleDraft,
    emailDraft,
    setEmailDraft,
    screenFilter,
    setScreenFilter,
    endpointFilter,
    setEndpointFilter,
    addRole,
    removeRole,
    addEmail,
    removeEmail,
    toggleScreenRole,
    setAllScreensForRole,
    toggleEndpointRole: (id, role) => setEndpoints((rows) => toggleRole(rows, id, role)),
    setAllEndpointRoles: (id, checked) =>
      setEndpoints((rows) => setAllRoles(rows, id, matrixRoles, checked)),
    handleSave,
  };
}
